import React, { useState, useCallback } from 'react';
import { Icon } from '@ontruck/orc';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDropzone } from 'react-dropzone';
import http from 'utils/network/http';
import { readFile } from 'utils/functions';
import ProgressBar from 'components/ProgressBar';
import cx from 'classnames';
import { MAX_FILE_SIZE } from 'utils/constants/documents';

const ACCEPTED_IMAGES = ['image/jpg', 'image/jpeg'];
const ACCEPTED_PDF = ['application/pdf'];
const checkIfThereAreImages = files =>
  files.some(file => ACCEPTED_IMAGES.includes(file.type));
const checkIfThereArePdf = files =>
  files.some(file => ACCEPTED_PDF.includes(file.type));
const checkIfIsImage = file => ACCEPTED_IMAGES.includes(file.type);

const checkFileSize = file => file.size <= MAX_FILE_SIZE;

const MODES = {
  PDF: 'PDF',
  IMAGE: 'IMAGE',
  BOTH: 'BOTH'
};

const PodUpload = ({
  className,
  title,
  manifest,
  addFuctionToUpload,
  toggleError,
  removeUrlToSend
}) => {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [mode, setMode] = useState(MODES.BOTH);
  const [disabled, setDisabled] = useState(false);
  const { t } = useTranslation('Pods');

  const addProgressToFile = useCallback((file, progressEvent, files) => {
    const percentCompleted = Math.round(
      (progressEvent.loaded * 100) / progressEvent.total
    );
    const newFiles = [...files];
    const index = newFiles.indexOf(file);
    if (index !== -1) {
      newFiles[index].progress = percentCompleted;
      setUploadedFiles([...newFiles]);
    }
  }, []);

  const updateModes = useCallback(newFiles => {
    if (checkIfThereAreImages(newFiles)) {
      setMode(MODES.IMAGE);
    } else if (checkIfThereArePdf(newFiles)) {
      setMode(MODES.PDF);
    } else {
      setMode(MODES.BOTH);
    }
  }, []);

  const deleteImage = useCallback(
    async ordinal => {
      try {
        await http.delete(`${manifest.images_url}${ordinal}`);
      } catch (err) {
        console.error('Error deleting image', err);
      }
    },
    [manifest.images_url]
  );

  const removeFile = useCallback(
    file => () => {
      const newFiles = [...uploadedFiles];
      const fileOrdinal = newFiles.indexOf(file);
      if (checkIfIsImage(file) && !file.error) {
        deleteImage(fileOrdinal);
      } else {
        setDisabled(false);
      }
      newFiles.splice(fileOrdinal, 1);
      setUploadedFiles(newFiles);
      updateModes(newFiles);
      removeUrlToSend(file.name, title);
      if (!checkFileSize(file)) {
        toggleError(false);
      }
    },
    [
      uploadedFiles,
      updateModes,
      deleteImage,
      toggleError,
      removeUrlToSend,
      title
    ]
  );

  const addErrorToFile = useCallback((file, files) => {
    const newFiles = [...files];
    const index = newFiles.indexOf(file);
    if (index !== -1) {
      newFiles[index].error = true;
      setUploadedFiles([...newFiles]);
    }
  }, []);

  const uploadImages = useCallback(
    async (file, newFiles) => {
      try {
        const config = {
          onUploadProgress: progressEvent => {
            addProgressToFile(file, progressEvent, newFiles);
          }
        };
        const ordinal = newFiles.length - 1;
        const imageBase = await readFile(file);
        await http.post(
          manifest.images_url,
          {
            image: imageBase,
            ordinal
          },
          config
        );
      } catch (err) {
        addErrorToFile(file, newFiles);
        console.error('UploadImages', err);
      }
    },
    [manifest.images_url, addProgressToFile, addErrorToFile]
  );

  const uploadPdfs = useCallback(
    async file => {
      try {
        const pdfBase = await readFile(file);
        addFuctionToUpload({
          stopTitle: title,
          fileName: file.name,
          func: http.post.bind(null, manifest.cargo_manifest_url, {
            files: [pdfBase],
            is_pdf: true
          })
        });
      } catch (err) {
        console.error('UploadImages', err);
      }
    },
    [addFuctionToUpload, manifest.cargo_manifest_url, title]
  );

  const onDrop = useCallback(
    acceptedFiles => {
      const newFiles = [...uploadedFiles, ...acceptedFiles];
      setUploadedFiles(newFiles);
      acceptedFiles.forEach((file, index) => {
        if (checkFileSize(file)) {
          if (checkIfIsImage(file)) {
            setMode(MODES.IMAGE);
            uploadImages(file, newFiles);
            addFuctionToUpload({
              stopTitle: title,
              fileName: file.name,
              func: http.post.bind(null, manifest.cargo_manifest_url)
            });
          } else {
            setMode(MODES.PDF);
            setDisabled(true);
            uploadPdfs(file);
          }
        } else {
          toggleError(true);
        }
      });
    },
    [
      uploadedFiles,
      addFuctionToUpload,
      manifest.cargo_manifest_url,
      toggleError,
      uploadImages,
      uploadPdfs,
      title
    ]
  );

  const {
    getRootProps,
    getInputProps,
    isDragAccept,
    isDragReject
  } = useDropzone({
    onDrop,
    accept: [
      ...(mode === MODES.IMAGE ? ACCEPTED_IMAGES : []),
      ...(mode === MODES.PDF ? ACCEPTED_PDF : []),
      ...(mode === MODES.BOTH ? [...ACCEPTED_IMAGES, ...ACCEPTED_PDF] : [])
    ],
    disabled,
    disableClick: disabled
  });

  return (
    <div className={className} data-testid="pod-upload">
      <div className="pod-upload">
        <div className="pod-upload__title">{title}</div>
        <div
          {...getRootProps({
            className: `pod-upload__droparea ${cx({
              'pod-upload__droparea--disabled': disabled,
              'pod-upload__droparea--accept': isDragAccept
            })}`
          })}
        >
          <input {...getInputProps()} data-testid="pod-upload__input" />
          {!isDragAccept && !isDragReject && (
            <>
              <div className="pod-upload__drop-help">{t('dragDocument')}</div>
              <button className="pod-upload__drop-button">
                {t('uploadHere')}
              </button>
            </>
          )}
          {isDragAccept && (
            <div className="pod-upload__drop-help">{t('dragOver')}</div>
          )}
        </div>
        <div className="pod-upload_file-container">
          {uploadedFiles.map(file => {
            return (
              <div className="pod-upload__file" key={file.path}>
                <div className="pod-upload__file-info">
                  <div
                    className={`pod-upload__file-name ${
                      file.error ? 'pod-upload__file-name--error' : ''
                    }`}
                  >
                    {file.path}
                  </div>
                  <button
                    className="pod-upload__file-delete"
                    onClick={removeFile(file)}
                    data-testid="pod-upload__file-delete"
                  >
                    <Icon glyph="trash" size="1.6rem" />
                  </button>
                </div>
                {file.progress && !file.error && (
                  <div className="pod-upload__progress">
                    <ProgressBar
                      percentage={file.progress}
                      text={t('upload')}
                    />
                  </div>
                )}
                {(!checkFileSize(file) || file.error) && (
                  <div className="pod-upload__error">
                    <span className="pod-upload__error-label">
                      {t('errorTitle')}
                    </span>
                    <div className="pod-upload__error-container">
                      <span className="pod-upload__error-text">
                        {!checkFileSize(file)
                          ? t('sizeError')
                          : t('generalError')}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
PodUpload.propTypes = {
  className: PropTypes.string,
  addFuctionToUpload: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  manifest: PropTypes.object.isRequired,
  toggleError: PropTypes.func.isRequired,
  removeUrlToSend: PropTypes.func.isRequired
};
export default PodUpload;
